import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./SendUpdate.module.css";
import LoadingWaves from "../../components/common/LoadingWaves";
import { useOnboarding } from "../../hooks/useOnboarding";
import { useOnboardingFlow } from "../../hooks/useOnboardingFlow";
import { workflowService } from "../../services/workflowService";
import { emailUpdateService } from "../../services/emailUpdateService";
import { SimpleEditor } from "../../@/components/tiptap-templates/simple/simple-editor";
import { JSONContent } from "@tiptap/core/dist/types";
import supabaseClient from "../../lib/supabaseClient";
// lightweight inline confetti burst (black/gray/white)
const fireConfetti = (colors: string[]) => {
  const canvas = document.createElement("canvas");
  canvas.style.position = "fixed";
  canvas.style.top = "0";
  canvas.style.left = "0";
  canvas.style.width = "100%";
  canvas.style.height = "100%";
  canvas.style.pointerEvents = "none";
  canvas.style.zIndex = "9999";
  document.body.appendChild(canvas);
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const W = (canvas.width = window.innerWidth);
  const H = (canvas.height = window.innerHeight);
  const N = 120;
  const particles = Array.from({ length: N }).map(() => ({
    x: W / 2,
    y: H * 0.4 + Math.random() * H * 0.2,
    vx: (Math.random() - 0.5) * 8,
    vy: Math.random() * -6 - 2,
    g: 0.15 + Math.random() * 0.05,
    size: 3 + Math.random() * 3,
    color: colors[Math.floor(Math.random() * colors.length)],
    life: 60 + Math.floor(Math.random() * 40),
  }));

  let frame = 0;
  const animate = () => {
    frame++;
    ctx.clearRect(0, 0, W, H);
    particles.forEach((p) => {
      p.vy += p.g;
      p.x += p.vx;
      p.y += p.vy;
      p.life--;
      ctx.fillStyle = p.color;
      ctx.fillRect(p.x, p.y, p.size, p.size);
    });
    if (frame < 120) requestAnimationFrame(animate);
    else document.body.removeChild(canvas);
  };
  animate();
};

// Local storage keys for caching editor state on SendUpdate step
const LOCAL_SUBJECT_KEY = "onboarding_sendupdate_subject";
const LOCAL_BODY_KEY = "onboarding_sendupdate_body";
const LOCAL_SENT_KEY = "onboarding_sendupdate_sent";

interface SendUpdateProps {
  onNext: (data?: any) => void;
  onBack?: () => void;
}

const SendUpdate: React.FC<SendUpdateProps> = ({ onNext, onBack }) => {
  const { data } = useOnboarding();
  const { data: flowData, save } = useOnboardingFlow();
  const [isLoading, setIsLoading] = useState(true);
  const [initComplete, setInitComplete] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  const lastSavedSubjectRef = useRef<string | null>(null);
  const lastSavedBodyRef = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [subject, setSubject] = useState<string>("");
  const [body, setBody] = useState<string | JSONContent | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const prevFetchKeyRef = React.useRef<string | null>(null);

  // Compute recipient email with strong fallback order
  const recipientEmail = useMemo(() => {
    const fromState = userEmail && String(userEmail).trim();
    const fromOnboarding =
      (data as any)?.email && String((data as any)?.email).trim();
    const fromFlow =
      (flowData as any)?.email && String((flowData as any)?.email).trim();
    return (fromState || fromOnboarding || fromFlow || "").trim();
  }, [userEmail, data, flowData]);

  // Initialize recipient email early and refresh from Supabase
  useEffect(() => {
    let mounted = true;
    try {
      const seed = String(
        ((data as any)?.email || (flowData as any)?.email || "") as any
      ).trim();
      if (seed && !userEmail) setUserEmail(seed);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.debug("SendUpdate: failed to seed recipient from onboarding", e);
    }
    (async () => {
      try {
        const { data: userData } = await supabaseClient.auth.getUser();
        const email = userData?.user?.email || null;
        if (mounted && email) setUserEmail(email);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.debug("SendUpdate: failed to fetch user via auth.getUser", e);
      }
      try {
        const { data: sessionData } = await supabaseClient.auth.getSession();
        const emailFromSession = sessionData?.session?.user?.email || null;
        if (mounted && emailFromSession) setUserEmail(emailFromSession);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.debug(
          "SendUpdate: failed to fetch user via auth.getSession",
          e
        );
      }
    })();
    return () => {
      mounted = false;
    };
  }, [data, flowData, userEmail]);

  const selectedInsights = useMemo(() => {
    const list =
      (flowData as any)?.selectedInsights ||
      (data as any)?.selectedInsights ||
      [];
    // Prefer IDs when available
    const ids = list
      .map((it: any) => (typeof it === "string" ? it : it?.id))
      .filter(Boolean);
    return ids.length > 0 ? ids : list;
  }, [flowData, data]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // First: restore from localStorage to avoid unnecessary generation request
        try {
          const cachedSubject = localStorage.getItem(LOCAL_SUBJECT_KEY);
          const cachedBodyRaw = localStorage.getItem(LOCAL_BODY_KEY);
          const cachedSent = localStorage.getItem(LOCAL_SENT_KEY) === "1";
          if (mounted && (cachedSubject || cachedBodyRaw)) {
            if (cachedSubject) setSubject(cachedSubject);
            if (cachedBodyRaw) {
              let parsed: any = null;
              try {
                parsed = JSON.parse(cachedBodyRaw);
              } catch (_e) {
                // not JSON, treat as string HTML
              }
              setBody(parsed ?? cachedBodyRaw);
            }
            setSent(
              cachedSent ||
                Boolean(
                  (flowData as any)?.send_update_sent ||
                    (data as any)?.send_update_sent
                )
            );
            setHasContent(true);
            setIsLoading(false);
            setError(null);
            setInitComplete(true);
            return; // short-circuit: do not call generation API
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.debug("SendUpdate: failed to read local cache", e);
        }

        // Compute effective insights; avoid empty/duplicate fetches to stop flicker
        const effectiveInsights =
          (Array.isArray((flowData as any)?.selectedInsights) &&
          (flowData as any)?.selectedInsights?.length > 0
            ? (flowData as any)?.selectedInsights
            : undefined) ||
          (Array.isArray((data as any)?.selectedInsights) &&
          (data as any)?.selectedInsights?.length > 0
            ? (data as any)?.selectedInsights
            : undefined) ||
          selectedInsights;

        const fetchKey = JSON.stringify(effectiveInsights || []);

        // If nothing to fetch, clear error/loading and bail
        if (
          !effectiveInsights ||
          (Array.isArray(effectiveInsights) && effectiveInsights.length === 0)
        ) {
          if (mounted) {
            setIsLoading(false);
            setError(null);
            setInitComplete(true);
          }
          return;
        }

        // Skip duplicate fetches for same payload to prevent brief error flash
        if (prevFetchKeyRef.current === fetchKey) {
          return;
        }
        prevFetchKeyRef.current = fetchKey;

        setError(null);
        setIsLoading(true);
        // Recipient fetching handled by separate effect; no-op here
        const result = await emailUpdateService.generateEmailFromInsights(
          effectiveInsights
        );
        if (!mounted) return;
        const resData = (result as any)?.data || result;
        setSubject(
          (flowData as any)?.generated_subject ||
            (data as any)?.generated_subject ||
            resData?.subject ||
            "Your First Update"
        );
        setBody(
          (flowData as any)?.generated_body ||
            (data as any)?.generated_body ||
            resData?.body ||
            ""
        );
        setSent(
          Boolean(
            (flowData as any)?.send_update_sent ||
              (data as any)?.send_update_sent
          )
        );
        setHasContent(true);
        // Persist freshly generated content to local cache
        try {
          localStorage.setItem(
            LOCAL_SUBJECT_KEY,
            String(
              (flowData as any)?.generated_subject ||
                (data as any)?.generated_subject ||
                resData?.subject ||
                "Your First Update"
            )
          );
          const bodyToStore =
            (flowData as any)?.generated_body ||
            (data as any)?.generated_body ||
            resData?.body ||
            "";
          localStorage.setItem(
            LOCAL_BODY_KEY,
            typeof bodyToStore === "string"
              ? bodyToStore
              : JSON.stringify(bodyToStore)
          );
        } catch (e) {
          // eslint-disable-next-line no-console
          console.debug(
            "SendUpdate: failed to write local cache after generate",
            e
          );
        }
        setError(null);
      } catch (e: any) {
        setError(e?.message || "Failed to generate email content");
      } finally {
        setIsLoading(false);
        setInitComplete(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [selectedInsights, flowData, data]);

  // Debounce persist of subject/body to onboarding data so refresh restores exact content
  useEffect(() => {
    // Only persist after initialization and when content is present
    if (!initComplete || !hasContent) return;
    const t = setTimeout(async () => {
      const normalizedBody =
        typeof body === "string" ? body || "" : JSON.stringify(body || "");
      const subjChanged = subject !== (lastSavedSubjectRef.current || "");
      const bodyChanged = normalizedBody !== (lastSavedBodyRef.current || "");
      const flowSubj = String((flowData as any)?.generated_subject || "");
      const flowBody =
        typeof (flowData as any)?.generated_body === "string"
          ? (flowData as any)?.generated_body || ""
          : JSON.stringify((flowData as any)?.generated_body || "");
      const differsFromFlow =
        subject !== flowSubj || normalizedBody !== flowBody;

      if (subjChanged || bodyChanged || differsFromFlow) {
        try {
          await save({
            generated_subject: subject,
            generated_body: body,
          } as any);
          lastSavedSubjectRef.current = subject || "";
          lastSavedBodyRef.current = normalizedBody;
        } catch (err) {
          // eslint-disable-next-line no-console
          console.debug("Onboarding: failed to persist editor content", err);
        }
      }

      // Always update local cache promptly for reload resilience
      try {
        localStorage.setItem(LOCAL_SUBJECT_KEY, subject || "");
        localStorage.setItem(LOCAL_BODY_KEY, normalizedBody);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.debug("SendUpdate: failed to write local cache on edit", e);
      }
    }, 1500);
    return () => clearTimeout(t);
  }, [subject, body, initComplete, hasContent]);

  // Tick down the cooldown once per second
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(
      () => setResendCooldown((c) => Math.max(0, c - 1)),
      1000
    );
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleSend = async () => {
    try {
      setIsSending(true);
      setError(null);
      // Send to the current user's email via one-off Mailgun endpoint
      const recipient =
        userEmail || (data as any)?.email || (flowData as any)?.email;
      if (!recipient) throw new Error("No recipient email found");

      console.log(`[SendUpdate] Sending email to: ${recipient}`);

      const sendRes = await emailUpdateService.sendOneOffEmail({
        contacts: [{ email: String(recipient) }],
        subject: subject || "Your First Update",
        body: body || "",
      });

      console.log("[SendUpdate] Email send response:", sendRes);

      if ((sendRes as any)?.success === false) {
        const errorMsg = (sendRes as any)?.error || "Failed to send update";
        console.error("[SendUpdate] Email send failed:", errorMsg);
        throw new Error(errorMsg);
      }

      console.log("[SendUpdate] Email sent successfully!");
      setSent(true);
      setResendCooldown(20);

      try {
        const completed = new Set([
          ...(((flowData as any)?.completed_steps ||
            (data as any)?.completed_steps ||
            []) as any[]),
          "prepare_update",
          "send_update",
        ]);
        await save({
          send_update_sent: true,
          generated_subject: subject,
          generated_body: body,
          step: 7,
          completed_steps: Array.from(completed),
        } as any);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.debug("Onboarding: failed to persist send confirmation", err);
      }
      // Mark sent in local cache
      try {
        localStorage.setItem(LOCAL_SENT_KEY, "1");
        localStorage.setItem(LOCAL_SUBJECT_KEY, subject || "");
        localStorage.setItem(
          LOCAL_BODY_KEY,
          typeof body === "string" ? body || "" : JSON.stringify(body || "")
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.debug("SendUpdate: failed to mark sent in local cache", e);
      }
      // fire black/gray/white confetti
      const colors = ["#000000", "#9ca3af", "#ffffff"];
      fireConfetti(colors);
    } catch (e: any) {
      const errorMsg = e?.message || "Failed to send update";
      console.error("[SendUpdate] Error:", errorMsg);
      setError(errorMsg);
    } finally {
      setIsSending(false);
    }
  };

  // Normalize backend body into TipTap-friendly input (JSON or HTML)
  const editorInitialContent = useMemo(() => {
    // Helper: when backend gives TipTap JSON but text nodes include **bold** marks,
    // split text nodes into bold/non-bold segments while keeping only valid TipTap nodes.
    const normalizeJsonBold = (doc: any): any => {
      if (!doc || typeof doc !== "object") return doc;

      const splitBoldSegments = (text: string) => {
        const parts: Array<{ text: string; bold: boolean }> = [];
        const regex = /(\*\*|__)(.+?)\1/g;
        let lastIndex = 0;
        let match: RegExpExecArray | null;
        while ((match = regex.exec(text)) !== null) {
          const [full, _marker, inner] = match;
          const start = match.index;
          if (start > lastIndex)
            parts.push({ text: text.slice(lastIndex, start), bold: false });
          parts.push({ text: inner, bold: true });
          lastIndex = start + full.length;
        }
        if (lastIndex < text.length)
          parts.push({ text: text.slice(lastIndex), bold: false });
        return parts.filter((p) => p.text && p.text.length > 0);
      };

      const walk = (node: any): any => {
        if (!node || typeof node !== "object") return node;
        if (Array.isArray(node.content)) {
          const next: any[] = [];
          for (const child of node.content) {
            if (
              child &&
              child.type === "text" &&
              typeof child.text === "string" &&
              (/\*\*.+?\*\*/.test(child.text) || /__.+?__/.test(child.text))
            ) {
              const segs = splitBoldSegments(child.text).map((seg) => ({
                type: "text",
                text: seg.text,
                ...(seg.bold ? { marks: [{ type: "bold" }] } : {}),
              }));
              next.push(...segs);
            } else {
              next.push(walk(child));
            }
          }
          return { ...node, content: next };
        }
        return node;
      };

      return walk(doc);
    };

    const value = body as any;
    if (!value) return "";
    if (typeof value !== "string") {
      // Attempt to normalize bold markers within JSON text nodes
      try {
        return normalizeJsonBold(value);
      } catch (err) {
        return value;
      }
    }
    const text = value.trim();
    // Try JSON
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === "object" && parsed.type) {
        return normalizeJsonBold(parsed as JSONContent);
      }
    } catch (err) {
      // Not JSON; continue with HTML/markdown normalization
    }
    // If looks like HTML, pass through
    if (/[<][a-zA-Z][\s\S]*[>]/.test(text)) return text;
    // Basic Markdown -> HTML normalization (bold/italic/newlines)
    // Bold **text** or __text__
    let html = text
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/__(.+?)__/g, "<strong>$1</strong>");
    // Italic *text* or _text_ (avoid already converted strong)
    html = html.replace(/(^|\W)\*(?!\*)([^*]+)\*(?=\W|$)/g, "$1<em>$2</em>");
    html = html.replace(/(^|\W)_([^_]+)_(?=\W|$)/g, "$1<em>$2</em>");
    // Paragraphs and line breaks
    const paragraphs = html
      .split(/\n{2,}/)
      .map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`) // single newlines -> <br>
      .join("");
    return paragraphs || html;
  }, [body]);

  if (!hasContent) {
    return (
      <div className={styles.root}>
        <h2 className={styles.header}>Send Your Email Now</h2>
        <p className={styles.subheader}>
          This email will be sent to your own inbox so you can see
          Sendupdates.ai in action.
        </p>
        <LoadingWaves label="preparing your email preview..." />
      </div>
    );
  }

  if (sent) {
    return (
      <div className={styles.root}>
        <h2 className={styles.header}>Email Sent</h2>
        <p className={styles.subheader}>
          This is exactly the kind of update your investors/leaders/team will
          receive.
        </p>
        <div
          className={styles.recipientBox}
          style={{ border: "1px solid #ffffff", marginBottom: "16px" }}
        >
          <strong>Email sent to:</strong>{" "}
          {String(recipientEmail || "your email")}
        </div>
        <p style={{ fontSize: "14px", color: "#9ca3af", marginBottom: "16px" }}>
          <strong>Note:</strong> Check your spam folder if you don&apos;t see
          it. Emails are sent from <strong>update@mail.sendupdates.ai</strong>.
        </p>
        {error && (
          <div
            className={styles.panel}
            style={{
              borderColor: "#ef4444",
              color: "#ef4444",
              marginBottom: "16px",
            }}
          >
            {error}
          </div>
        )}
        <div className={styles.footer}>
          <button
            className={`${styles.button}`}
            style={{ width: "100%", marginBottom: "12px" }}
            onClick={handleSend}
            disabled={isSending || resendCooldown > 0}
          >
            {isSending
              ? "Sending..."
              : resendCooldown > 0
              ? `Send Email Again (${resendCooldown}s)`
              : "Send Email Again"}
          </button>
          <button
            className={`${styles.button} ${styles.primary}`}
            style={{ width: "100%" }}
            onClick={() => onNext()}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <h2 className={styles.header}>Send Your Email Now</h2>
      <p className={styles.subheader}>
        This email will be sent to your own inbox so you can see Sendupdates.ai
        in action.
      </p>

      <div
        className={styles.recipientBox}
        style={{ border: "1px solid #ffffff" }}
      >
        <strong>Recipient:</strong> {String(recipientEmail || "your email")}
      </div>

      <div className={styles.panel}>
        <div style={{ marginBottom: 8 }}>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject"
            style={{
              width: "100%",
              background: "#000",
              color: "#fff",
              border: "1px solid #fff",
              padding: 8,
            }}
          />
        </div>
        <div
          className={`${"tiptap-black-text"} ${styles.tiptapDark}`}
          style={{ background: "#000" }}
        >
          <SimpleEditor
            initialContent={editorInitialContent}
            onChange={(content) => setBody(content)}
          />
        </div>
      </div>

      <div className={styles.footer}>
        {error && (
          <div
            className={styles.panel}
            style={{ borderColor: "#ef4444", color: "#ef4444" }}
          >
            {error}
          </div>
        )}
        <button
          className={`${styles.button} ${styles.primary}`}
          style={{ width: "100%" }}
          onClick={handleSend}
          disabled={isSending || !recipientEmail}
        >
          {isSending ? "Sending..." : "Send Email"}
        </button>
      </div>
    </div>
  );
};

export default SendUpdate;
